import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { setQrCode } from "@/redux/slices/qrSlice";
import { useNavigate } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Loader2, ScanLine } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState, useCallback } from "react";
import { getData, transactions, updateStatus } from "@/services/customer";
import { useTranslation } from "react-i18next";
import jsQR from "jsqr";

export default function Home() {
  const { t } = useTranslation();
  const name = localStorage.getItem("name");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showCodePopup, setShowCodePopup] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [voucherValue, setVoucherValue] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [showMinBalPopup, setShowMinBalPopup] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false); // prevent double-scan

  const { data: customer, loading } = useSelector((state) => state.customer);
  const { code: qrCode } = useSelector((state) => state.qr);
  const token = useSelector((state) => state.auth.token);

  const lastProcessedCode = useRef(null);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "greeting.morning";
    if (hour >= 12 && hour < 17) return "greeting.afternoon";
    return "greeting.evening";
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    return words.length === 1 ? words[0][0] : words[0][0] + words[1][0];
  };

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    if (!qrCode || !token) return;
    if (lastProcessedCode.current === qrCode) return;
    lastProcessedCode.current = qrCode;
    processQrCode(qrCode);
  }, [qrCode, token]);

  // ── QR Scanner — same as ScanQR.jsx approach ──
  const extractCodeFromUrl = (raw) => {
    try {
      const url = new URL(raw);
      const parts = url.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      if (last && last.toUpperCase().startsWith("QR")) return last.toUpperCase();
      return last ? last.toUpperCase() : raw.trim().toUpperCase();
    } catch {
      return raw.trim().toUpperCase();
    }
  };

  const stopScanner = useCallback(() => {
    scanningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startScanner = useCallback(async () => {
    setCameraReady(false);
    scanningRef.current = true;
    setShowScanner(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;

      const attach = () => {
        const video = videoRef.current;
        if (!video) { setTimeout(attach, 100); return; }
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          video.play().then(() => {
            setCameraReady(true);
            scanLoop();
          }).catch(console.error);
        };
      };
      setTimeout(attach, 150);
    } catch (err) {
      scanningRef.current = false;
      setShowScanner(false);
      console.error("Camera error:", err.name, err.message);
    }
  }, []);

  const scanLoop = () => {
    if (!scanningRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      requestAnimationFrame(scanLoop);
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const result = jsQR(imageData.data, imageData.width, imageData.height);
    if (result && scanningRef.current) {
      scanningRef.current = false;
      const code = extractCodeFromUrl(result.data);
      stopScanner();
      setShowScanner(false);
      lastProcessedCode.current = null;
      dispatch(setQrCode(null));
      requestAnimationFrame(() => dispatch(setQrCode(code)));
    } else {
      requestAnimationFrame(scanLoop);
    }
  };

  const handleCloseScanner = () => {
    scanningRef.current = false;
    stopScanner();
    setShowScanner(false);
  };

  // ── QR Process ──
  const processQrCode = async (code) => {
    setQrLoading(true);

    const showError = (msgKey) => {
      setStatus("error");
      setMessage(msgKey);
      setQrLoading(false);
      setShowPopup(true);
    };

    try {
      const qrData = await getData(token, code);
      const unitData = qrData?.data?.unitData?.[0];
      const qrTemplateData = qrData?.data?.qrTemplateData;

      if (!qrData || qrData?.error || !unitData) { showError("voucher.invalid"); return; }
      if (unitData?.isAssigned === true) { showError("voucher.used"); return; }
      if (unitData?.status !== "Active") { showError("voucher.invalid"); return; }
      if (new Date(qrTemplateData?.expiryDate) < new Date()) { showError("voucher.expired"); return; }

      const value = qrTemplateData?.value ?? 0;
      const mobileRaw = localStorage.getItem("mobile") || "";
      const mobile = mobileRaw.replace(/^(\+91|\+91\s?)/, "");
      const customerCode = localStorage.getItem("customercode");

      const body = {
        activity: "QR Scan", amount: value, mobile,
        schemacode: "TSC000329", qrcode: code,
        productName: qrTemplateData?.productName,
        quantity: qrTemplateData?.quantity,
        scanningtype: "QR Scaned", scanningregion: "india",
      };

      const transRes = await transactions(token, body);

      if (transRes?.message === "Request processed successfully.") {
        await updateStatus(token, unitData?.code, "inActive");
        dispatch(fetchCustomerData(customerCode));
        setVoucherValue(value);
        setStatus("success");
        setMessage("voucher.success");
      } else {
        setStatus("error");
        setMessage("voucher.invalid");
      }
    } catch {
      setStatus("error");
      setMessage("voucher.invalid");
    } finally {
      setQrLoading(false);
      setShowPopup(true);
    }
  };

  const availablePoints = customer?.walletsDetail?.TotalavailableValue || 0;

  const handleRedeemCash = () => {
    if (availablePoints <= 0) { setShowMinBalPopup(true); return; }
    navigate("/redeem-cash");
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    dispatch(setQrCode(null));
    localStorage.removeItem("qrCode");
  };

  const handleManualSubmit = () => {
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    lastProcessedCode.current = null;
    dispatch(setQrCode(null));
    requestAnimationFrame(() => dispatch(setQrCode(code)));
    setShowCodePopup(false);
    setManualCode("");
  };

  const apiTransactions = customer?.transactions || [];
  const recentTxs = [...apiTransactions]
    .sort((a, b) => new Date(b.transactionProcessedDate) - new Date(a.transactionProcessedDate))
    .slice(0, 5)
    .map((txItem) => {
      const detail = txItem.transactionDetail;
      const type = detail?.transactionType;
      const customerQualified =
        txItem.qualifiedCampaign?.find((q) => q.walletFor === "Customer")?.value || 0;

      let points = 0, isPositive = false;
      if (type === "purchase") { points = Math.abs(customerQualified); isPositive = true; }
      else if (type === "redeem") {
        points = Math.abs(detail?.values || detail?.amount || detail?.rewardpoint || customerQualified);
        isPositive = false;
      } else { points = Math.abs(customerQualified); isPositive = customerQualified > 0; }

      const dateObj = new Date(txItem.transactionProcessedDate);
      return {
        id: txItem.id,
        productName: detail?.productName || null,
        quantity: detail?.quantity || null,
        qrcode: detail?.qrcode || null,
        date: dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
        time: dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        points,
        isPositive,
        bankdetails: detail?.bankdetails,
      };
    });

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex justify-center">
      <div className="w-full max-w-md bg-white min-h-screen md:rounded-3xl md:shadow-lg">

        {(loading || qrLoading) && (
          <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
            <Loader2 className="animate-spin h-12 w-12" style={{ color: "#1A1F36" }} />
          </div>
        )}

        {/* Header */}
        <div className="px-4 pt-4">
          <div
            className="rounded-3xl p-5 mb-4 shadow-lg relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1A1F36, #2B3A67)" }}
          >
            {/* Top Section */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs text-white/70">{t(getGreeting())}</p>
                <p className="text-xl font-semibold text-white">
                  {name}
                </p>
              </div>

              <div
                onClick={() => navigate("/profile")}
                className="w-12 h-12 rounded-full flex items-center justify-center uppercase text-white font-semibold cursor-pointer border border-white/20 bg-white/10 backdrop-blur-sm"
              >
                {getInitials(name)}
              </div>
            </div>

            {/* Wallet Balance */}
            <div>
              <p className="text-xs text-white/70 mb-1">
                {t("wallet.cashbackBalance")}
              </p>

              <h1 className="text-3xl font-bold text-white tracking-wide">
                {customer?.walletsDetail?.TotalavailableValue || 0}
                <span className="text-lg font-medium text-white/70 ml-1">
                  pts
                </span>
              </h1>
            </div>

            <div className="border-t border-white/10 my-4"></div>

            {/* Redeem Button */}
            <button
              onClick={handleRedeemCash}
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95"
              style={{ background: "#ffffff", color: "#1A1F36" }}
            >
              {t("wallet.redeemCash")}
            </button>

            {/* Background Decoration */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-white/5 rounded-full blur-xl pointer-events-none"></div>
          </div>
        </div>

<div className="px-4 mb-4">
  <div className="grid grid-cols-2 gap-3">
    {/* Enter Code Button */}
    <button
      onClick={() => setShowCodePopup(true)}
      className="py-4 rounded-2xl text-white font-semibold shadow-md transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
      style={{
        background:
          "linear-gradient(135deg, rgb(148, 157, 7), rgb(43, 58, 103))",
      }}
    >
      <i className="fa-solid fa-qrcode"></i>
      {t("home.enterCode")}
    </button>

    {/* Scan QR Button */}
    <button
      onClick={startScanner}
      type="button"
      className="py-4 rounded-2xl text-white font-semibold shadow-md transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
      style={{
        background:
          "linear-gradient(135deg, rgb(43, 58, 103), rgb(148, 157, 7))",
      }}
    >
      <ScanLine size={20} />
{t("home.scanQr")}
    </button>
  </div>
</div>

        {/* Transactions */}
        <div className="px-4 mb-4">
          <div className="flex justify-between mb-3">
            <p className="text-md font-semibold text-[#1A1F36]">{t("transactions.title")}</p>
            {recentTxs.length > 0 && (
              <button onClick={() => navigate("/transactions")} className="text-xs text-gray-500">
                {t("transactions.viewAll")}
              </button>
            )}
          </div>
          <div className="bg-white rounded-3xl shadow-md border overflow-hidden">
            {recentTxs.length === 0 && (
              <p className="text-center text-sm text-gray-400 py-6">{t("transactions.empty")}</p>
            )}
            {recentTxs.map((tx, i) => (
              <div
                key={tx.id}
                className={`flex items-center justify-between px-4 py-3 ${i !== recentTxs.length - 1 ? "border-b" : ""} hover:bg-gray-50 transition`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full ${tx.isPositive ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>
                    {tx.isPositive ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1F36] leading-tight">
                      {tx.productName || tx?.bankdetails}
                    </p>
                    {(tx.quantity || tx.qrcode) && (
                      <p className="text-[11px] text-gray-400 leading-tight">
                        {tx.quantity ? `Qty: ${tx.quantity}` : ""}
                        {tx.quantity && tx.qrcode ? "  •  " : ""}
                        {tx.qrcode || ""}
                      </p>
                    )}
                    <p className="text-[11px] text-gray-400 leading-tight">{tx.date} • {tx.time}</p>
                  </div>
                </div>
                <div className="text-right ml-2">
                  <p className={`text-sm font-bold whitespace-nowrap ${tx.isPositive ? "text-green-600" : "text-red-500"}`}>
                    {tx.isPositive ? "+" : "-"}{tx.points} pts
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual Code Popup */}
        {showCodePopup && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-[90%] max-w-sm rounded-2xl p-6 relative">
              <button onClick={() => { setShowCodePopup(false); setManualCode(""); }} className="absolute top-3 right-3 text-gray-500 text-xl">✖</button>
              <h2 className="text-lg font-semibold mb-4 text-center">{t("voucher.enterCode")}</h2>
              <input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                placeholder={t("voucher.enterPlaceholder")}
                className="w-full border rounded-xl px-4 py-3 text-sm mb-4 outline-none focus:ring-2 focus:ring-[#1A1F36]"
                autoFocus
              />
              <button onClick={handleManualSubmit} disabled={!manualCode.trim()} className="w-full text-white py-3 rounded-xl disabled:opacity-50" style={{ backgroundColor: "#1A1F36" }}>
                {t("common.submit")}
              </button>
            </div>
          </div>
        )}

        {/* Result Popup */}
        {showPopup && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-[90%] max-w-sm rounded-2xl p-6 relative text-center">
              <button onClick={handleClosePopup} className="absolute top-3 right-3 text-gray-500 text-xl">✖</button>
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 text-2xl ${status === "success" ? "bg-green-100" : "bg-red-100"}`}>
                {status === "success" ? "✔" : "✖"}
              </div>
              <h2 className="text-lg font-semibold mb-2">
                {status === "success" ? t("voucher.successTitle") : t("voucher.failedTitle")}
              </h2>
              <p className="text-sm text-gray-500 mb-4">{message && t(message)}</p>
              {status === "success" && voucherValue > 0 && (
                <>
                  <p className="text-green-600 font-semibold mb-3">
                    {t("voucher.earnedAmount", { amount: voucherValue })}
                  </p>
                  <button
                    onClick={handleClosePopup}
                    className="w-full text-white py-3 rounded-xl"
                    style={{ backgroundColor: "#1A1F36" }}
                  >
                    {t("wallet.close")}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Min Balance Popup */}
        {showMinBalPopup && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-[90%] max-w-sm rounded-2xl p-6 text-center relative">
              <button onClick={() => setShowMinBalPopup(false)} className="absolute top-3 right-3 text-gray-400 text-xl">✖</button>
              <div className="w-16 h-16 mx-auto rounded-full bg-yellow-100 flex items-center justify-center mb-4 text-2xl">⚠️</div>
              <h2 className="text-lg font-bold text-[#1A1F36] mb-2">{t("redeem.minBalanceTitle")}</h2>
              <p className="text-sm text-gray-500 mb-5">{t("wallet.noBalance")}</p>
              <button onClick={() => setShowMinBalPopup(false)} className="w-full py-3 rounded-xl text-white font-semibold" style={{ backgroundColor: "#1A1F36" }}>
                {t("common.done")}
              </button>
            </div>
          </div>
        )}

        {/* ✅ QR Scanner Modal — zxing same as ScanQR.jsx */}
        {showScanner && (
          <div className="fixed inset-0 bg-black z-50 flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 bg-black/80">
           <p className="text-white font-semibold text-base">
  {t("home.scanQrCode")}
</p>
              <button
                onClick={handleCloseScanner}
                type="button"
                className="text-white text-2xl w-9 h-9 flex items-center justify-center rounded-full bg-white/10 mr-5"
              >
                ✖
              </button>
            </div>

            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${cameraReady ? "opacity-100" : "opacity-0"}`}
              />

              {!cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="animate-spin h-10 w-10 text-white/60" />
                </div>
              )}

              {/* Scan frame overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-64 h-64">
                  <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-white rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-white rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-white rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-white rounded-br-lg" />
                  <div className="absolute left-2 right-2 h-0.5 bg-white/70 animate-bounce" style={{ top: "50%" }} />
                </div>
              </div>

              <div className="absolute bottom-8 left-0 right-0 text-center">
               <p className="text-white/80 text-sm">
  {cameraReady
    ? t("home.pointCamera")
    : t("home.startingCamera")}
</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}